import React, { useEffect, useState } from "react";
import LoadingSpinner from '../../ui/LoadingSpinner';
import ChartBase from '../ChartBase';
import { chartData } from '../data';
import type { ChartOptions } from '../data/types';
import type { ApexOptions } from 'apexcharts';
import { getCustomersWithAlarms } from '../../../services/bureauManagement/bureauManagement.service';
import { useAuthContext } from '../../../contexts/useAuthContext';

interface CustomerAlarm {
  customer: string;
  acknowledged: number;
  unacknowledged: number;
}

interface Props {
  data?: CustomerAlarm[];
  title?: string;
  subtitle?: string;
}

const NO_DATA_DELAY_MS = 500;

const hasItems = <T,>(arr?: T[]): arr is T[] => !!arr?.length;

const buildCategories = (source?: CustomerAlarm[]): string[] => {
  if (hasItems(source)) {
    return source.map(d => d.customer);
  }

  const fallback = chartData.options?.xaxis?.categories;
  if (Array.isArray(fallback)) {
    return fallback;
  }

  return [];
};

const buildSeries = (source?: CustomerAlarm[]): NonNullable<ApexOptions['series']> => {
  if (!hasItems(source)) {
    return [];
  }

  return [
    { name: 'Acknowledged', data: source.map(d => d.acknowledged ?? 0) },
    { name: 'Unacknowledged', data: source.map(d => d.unacknowledged ?? 0) },
  ];
};


type ChartSeries = NonNullable<ApexOptions['series']>;

function useCustomersChart(externalData?: CustomerAlarm[]) {
  const [source, setSource] = useState<CustomerAlarm[] | undefined>(externalData);
  const [loading, setLoading] = useState(!externalData);
  const [error, setError] = useState<string | null>(null);
  const [showNoData, setShowNoData] = useState(false);

  useEffect(() => {
    if (externalData) {
      setSource(externalData);
      return;
    }

    let canceled = false;
    setLoading(true);
    setError(null);

    getCustomersWithAlarms()
      .then(res => {
        if (canceled) { return; }
        if (res?.success && res.data?.customers?.length) {
          const mapped: CustomerAlarm[] = res.data.customers
            .filter(c => c.type === 'customer')
            .map(c => ({
              customer: c.title,
              acknowledged: c.activeAck ?? 0,
              unacknowledged: c.activeNotAck ?? 0,
            }))
            .filter(c => c.acknowledged + c.unacknowledged > 0);
          setSource(mapped);
        } else {
          setSource(undefined);
          if (!res?.success) {
            setError('Failed to load customer alarm data');
          }
        }
      })
      .catch(() => {
        if (!canceled) { setError('Failed to load customer alarm data'); }
      })
      .finally(() => { if (!canceled) { setLoading(false); } });

    return () => { canceled = true; };
  }, [externalData]);

  useEffect(() => {
    if (loading || source) {
      setShowNoData(false);
      return undefined;
    }
    const t = setTimeout(() => setShowNoData(true), NO_DATA_DELAY_MS);
    return () => clearTimeout(t);
  }, [loading, source]);

  return { source, loading, error, showNoData };
}

const renderContent = (
  source: CustomerAlarm[] | undefined,
  series: ChartSeries,
  options: ChartOptions,
  error: string | null,
  showNoData: boolean
) => {
  if (hasItems(source) && Array.isArray(series) && series.length > 0) {
    return (
      <ChartBase
        options={options}
        series={series}
        type="bar"
        height={350}
        width="100%"
        id="customerColumnChart"
      />
    );
  }

  if (error) {
    return <div className="status__error">{error}</div>;
  }

  if (showNoData) {
    return <div className="table-responsive-nodata-text">No Data</div>;
  }

  return <LoadingSpinner message="Loading chart data…" />;
};

const CustomerColumnChart: React.FC<Props> = ({
  data,
  title = 'Active Alarms by Customer',
  subtitle = 'Active Acknowledged vs Unacknowledged by Customer',
}) => {
  const { isAuthenticated, isLoading: authLoading } = useAuthContext();
  const authReady = isAuthenticated && !authLoading;

  const { source, error, showNoData } = useCustomersChart(authReady ? data : undefined);

  const categories = buildCategories(source);
  const series = buildSeries(source);

  const options = {
    ...chartData.options,
    chart: { ...(chartData.options?.chart || {}), id: 'customerLineChart' },
    xaxis: { ...(chartData.options?.xaxis || {}), categories },
    tooltip: { ...(chartData.options?.tooltip || {}), shared: true, intersect: false },
  };

  return (
    <div className="card">
      <div className="charts">
        <h3>{title}</h3>
        <h5>{subtitle}</h5>
        {renderContent(source, series, options, error, showNoData)}
      </div>
    </div>
  );
};

export default CustomerColumnChart;